import { useState, useEffect } from 'react';
import { Box, Paper, Typography, Button, Grid, Card, CircularProgress } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { getToken, revokeToken, getStravaToken, revokeStravaToken } from '../auth/AuthSlice';
import OauthPopup from '../auth/OAuthPopup';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LinkIcon from '@mui/icons-material/Link';
import RouteIcon from '@mui/icons-material/Route';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import UploadFileIcon from '@mui/icons-material/UploadFile';

function RouteSources() {
    const dispatch = useDispatch();
    const rwgpsAuth = useSelector((state) => state.auth.connections.rwgps);
    const stravaAuth = useSelector((state) => state.auth.connections.strava);
    const [rwgpsLoading, setRwgpsLoading] = useState(false);
    const [stravaLoading, setStravaLoading] = useState(false);
    const [rwgpsProcessing, setRwgpsProcessing] = useState(false);
    const [stravaProcessing, setStravaProcessing] = useState(false);

    useEffect(() => {
        if (rwgpsAuth) {
            setRwgpsLoading(false);
            setRwgpsProcessing(false);
        }
    }, [rwgpsAuth]);

    useEffect(() => {
        if (stravaAuth) {
            setStravaLoading(false);
            setStravaProcessing(false);
        }
    }, [stravaAuth]);

    const onCode = (code) => {
        setRwgpsProcessing(true);
        setRwgpsLoading(true);
        dispatch(getToken({ code })).finally(() => {
            setRwgpsLoading(false);
            setRwgpsProcessing(false);
        });
    };

    const onStravaCode = (code) => {
        setStravaProcessing(true);
        setStravaLoading(true);
        dispatch(getStravaToken({ code })).finally(() => {
            setStravaLoading(false);
            setStravaProcessing(false);
        });
    };

    const handleRWGPSConnect = () => {
        setRwgpsLoading(true);
    };

    const handleStravaConnect = () => {
        setStravaLoading(true);
    };

    function onClose() {
        if (!rwgpsProcessing) {
            setRwgpsLoading(false);
        }
        if (!stravaProcessing) {
            setStravaLoading(false);
        }
    }

    const handleDisconnectRWGPS = () => {
        dispatch(revokeToken());
    };

    const handleDisconnectStrava = () => {
        dispatch(revokeStravaToken());
    };

    return (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
                Route Sources
            </Typography>

            <Grid container spacing={2}>
                {/* Ride With GPS */}
                <Grid item xs={12} sm={6} md={4}>
                    <Card
                        sx={{
                            p: 2,
                            cursor: rwgpsAuth ? 'default' : 'pointer',
                            '&:hover': {
                                boxShadow: 3
                            },
                            border: rwgpsAuth ? '2px solid #4caf50' : '1px solid #e0e0e0',
                            minHeight: rwgpsAuth ? '140px' : 'auto',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <img
                                src="https://ridewithgps.com/images/revised_layout/logo.png"
                                alt="Ride With GPS Logo"
                                style={{ height: 24, marginRight: 8 }}
                            />
                            {rwgpsLoading && (
                                <CircularProgress size={20} sx={{ ml: 'auto' }} />
                            )}
                            {!rwgpsLoading && rwgpsAuth && (
                                <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20, ml: 'auto' }} />
                            )}
                        </Box>

                        {rwgpsAuth ? (
                            <Box sx={{ mt: 'auto' }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Connected - Access all your saved routes
                                </Typography>
                                <Button
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDisconnectRWGPS();
                                    }}
                                >
                                    Disconnect
                                </Button>
                            </Box>
                        ) : (
                            <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Connect to import your routes
                                </Typography>
                                <OauthPopup
                                    url={`https://ridewithgps.com/oauth/authorize?client_id=${process.env.REACT_APP_RWGPS_CLIENT_ID}&redirect_uri=${process.env.REACT_APP_PUBLIC_HOSTNAME}/rwgps-success&response_type=code`}
                                    onCode={onCode}
                                    onClose={onClose}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={rwgpsLoading ? <CircularProgress size={16} color="inherit" /> : <LinkIcon />}
                                        fullWidth
                                        size="small"
                                        disabled={rwgpsLoading}
                                        onClick={handleRWGPSConnect}
                                    >
                                        {rwgpsLoading ? 'Connecting...' : 'Connect'}
                                    </Button>
                                </OauthPopup>
                            </Box>
                        )}
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                    <Card
                        sx={{
                            p: 2,
                            cursor: 'pointer',
                            '&:hover': {
                                boxShadow: 3
                            },
                            border: '2px solid #4caf50',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <UploadFileIcon sx={{ color: '#666', fontSize: 24, mr: 1 }} />
                            <Typography variant="h6" sx={{ color: '#2c3e50', fontSize: '1rem' }}>
                                GPX Files
                            </Typography>
                            <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20, ml: 'auto' }} />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                            Upload GPX files directly
                        </Typography>
                    </Card>
                </Grid>

                {/* Strava Integration */}
                <Grid item xs={12} sm={6} md={4}>
                    <Card
                        sx={{
                            p: 2,
                            cursor: 'pointer',
                            '&:hover': {
                                boxShadow: 3
                            },
                            border: stravaAuth ? '2px solid #4caf50' : '1px solid #e0e0e0',
                            minHeight: stravaAuth ? '140px' : 'auto',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <img
                                src="https://developers.strava.com/images/strava_logo_nav.png"
                                alt="Strava Logo"
                                style={{ height: 24, marginRight: 8 }}
                            />
                            {stravaLoading && (
                                <CircularProgress size={20} sx={{ ml: 'auto' }} />
                            )}
                            {!stravaLoading && stravaAuth && (
                                <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20, ml: 'auto' }} />
                            )}
                        </Box>
                        {stravaAuth ? (
                            <Box sx={{ mt: 'auto' }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Connected - Access all your saved routes
                                </Typography>
                                <Button
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDisconnectStrava();
                                    }}
                                >
                                    Disconnect
                                </Button>
                            </Box>
                        ) : (
                            <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Connect to import your routes
                                </Typography>
                                <OauthPopup
                                    url={`https://www.strava.com/oauth/authorize?client_id=${process.env.REACT_APP_STRAVA_CLIENT_ID}&response_type=code&redirect_uri=${process.env.REACT_APP_PUBLIC_HOSTNAME}/strava-success&scope=read_all,activity:read_all&approval_prompt=force`}
                                    onCode={onStravaCode}
                                    onClose={onClose}>
                                    <Box
                                        onClick={handleStravaConnect}
                                        sx={{
                                            position: 'relative',
                                            display: 'inline-block',
                                            opacity: stravaLoading ? 0.6 : 1,
                                            cursor: stravaLoading ? 'not-allowed' : 'pointer'
                                        }}
                                    >
                                        {stravaLoading && (
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: '50%',
                                                    left: '50%',
                                                    transform: 'translate(-50%, -50%)',
                                                    zIndex: 1
                                                }}
                                            >
                                                <CircularProgress size={24} />
                                            </Box>
                                        )}
                                        <Box
                                            component="img"
                                            src="/strava/btn_strava_connect_with_orange.png"
                                            alt="Connect with Strava"
                                            sx={{
                                                height: 48,
                                                width: 'auto',
                                                display: 'block',
                                                maxWidth: '100%'
                                            }}
                                        />
                                    </Box>
                                </OauthPopup>
                            </Box>
                        )}
                    </Card>
                </Grid>



                <Grid item xs={12} sm={6} md={4}>
                    <Card sx={{ p: 2, backgroundColor: '#f8f9fa', border: '1px solid #e0e0e0' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <CloudDownloadIcon sx={{ color: '#666', fontSize: 24, mr: 1 }} />
                            <Typography variant="h6" sx={{ color: '#2c3e50', fontSize: '1rem' }}>
                                Komoot
                            </Typography>
                            <Typography variant="caption" sx={{ ml: 'auto', color: '#666' }}>
                                Coming Soon
                            </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                            Import from Komoot
                        </Typography>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                    <Card sx={{ p: 2, backgroundColor: '#f8f9fa', border: '1px solid #e0e0e0' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <RouteIcon sx={{ color: '#666', fontSize: 24, mr: 1 }} />
                            <Typography variant="h6" sx={{ color: '#2c3e50', fontSize: '1rem' }}>
                                Garmin
                            </Typography>
                            <Typography variant="caption" sx={{ ml: 'auto', color: '#666' }}>
                                Coming Soon
                            </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                            Connect Garmin Connect
                        </Typography>
                    </Card>
                </Grid>


            </Grid>
        </Paper>
    );
}

export default RouteSources;
